// Per-browser memory behind the ⌘K box's empty state:
//
//   recent visits   — the last pages / customers / orders / vessels opened
//                     (from search, the sidebar, or a detail page loading)
//   recent searches — the last queries that led somewhere
//   most visited    — what this person keeps coming back to
//
// One localStorage key holds a capped visit log; "most visited" is derived
// from it (no second counter to keep in sync). Every storage access is
// guarded: iOS Safari with "Block All Cookies" THROWS on localStorage, and
// history is a nicety that must never take the search box down with it.

const KEY = "chariot.search.history.v1";
const LOG_CAP = 60; // raw visits kept (the derived lists are much shorter)
export const RECENT_VISITS = 6;
export const RECENT_SEARCHES = 5;
export const MOST_VISITED = 5;

const listeners = new Set();
const read = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && Array.isArray(parsed.visits) && Array.isArray(parsed.searches) ? parsed : { visits: [], searches: [] };
  } catch {
    return { visits: [], searches: [] };
  }
};
const write = (state) => {
  try { window.localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* blocked storage */ }
  listeners.forEach((fn) => fn());
};

const keyOf = (item) => `${item.type}:${item.id}`;

/** A page or record was opened. `item` = { type, id, title, subtitle?, url }. */
export function recordVisit(item) {
  if (!item?.type || !item?.id || !item?.title || !item?.url) return;
  const state = read();
  const visit = { type: item.type, id: String(item.id), title: String(item.title), subtitle: item.subtitle || null, url: item.url, at: Date.now() };
  state.visits = [visit, ...state.visits].slice(0, LOG_CAP);
  write(state);
}

/** A query that led somewhere — remembered once, most recent first. */
export function recordSearch(query) {
  const q = String(query || "").trim();
  if (q.length < 2) return;
  const state = read();
  state.searches = [q, ...state.searches.filter((s) => s.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_SEARCHES);
  write(state);
}

/** The three lists the dropdown shows while the box is empty. */
export function getHistory() {
  const { visits, searches } = read();
  // Recent: newest first, one row per thing.
  const seen = new Set();
  const recent = [];
  for (const v of visits) {
    const k = keyOf(v);
    if (seen.has(k)) continue;
    seen.add(k);
    recent.push(v);
    if (recent.length >= RECENT_VISITS) break;
  }
  // Most visited: things opened at least twice, not already in Recent, by count.
  const counts = new Map();
  for (const v of visits) {
    const k = keyOf(v);
    const entry = counts.get(k) || { ...v, count: 0 };
    entry.count += 1;
    if (v.at > (entry.at || 0)) Object.assign(entry, v, { count: entry.count });
    counts.set(k, entry);
  }
  const frequent = [...counts.values()]
    .filter((e) => e.count >= 2 && !seen.has(keyOf(e)))
    .sort((a, b) => b.count - a.count || b.at - a.at)
    .slice(0, MOST_VISITED);
  return { recent, searches, frequent };
}

export function clearHistory() {
  write({ visits: [], searches: [] });
}

/** Re-render hook: fires after every write. Returns the unsubscribe. */
export function subscribeHistory(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
